<script setup lang="ts">
import Tag from '@components/Tag.vue';
import type { Submenu } from '@global-types/nav/menuItems';
import SubmenuItem from '@app/layout/nav/SubmenuItem.vue';
import { computed } from 'vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Icon from '@components/Icon.vue';
import IconCaretDown from '@icons/ph_caret-down.svg';
import Typography from '@components/Typography.vue';
import { trans } from '@tools/translation.ts';
import PlusMenuItem from '@app/layout/nav/mobile/components/PlusMenuItem.vue';
import MenuList from '@app/layout/nav/mobile/components/MenuList.vue';

interface MenuLinkProps {
  isDropdownOpen: boolean,
  hasPin?: boolean,
  hasNewTag?: boolean,
  submenus?: Submenu[],
}

const {
  isDropdownOpen: isDropdownOpenProp,
  hasPin = false,
  hasNewTag = false,
  submenus,
} = defineProps<MenuLinkProps>();

defineSlots<{
  icon(iconProps: { isActive: boolean }): typeof Icon,
  text(): unknown,
  submenus(): unknown,
}>();

const emit = defineEmits<{(e: 'toggleDropdown', isOpen: boolean): void }>();

const { isCurrentPage } = useNavbarStore();

const isDropdownOpen = computed<boolean>({
  get: () => isDropdownOpenProp,
  set: (isOpen) => emit('toggleDropdown', isOpen),
});

const isActive = computed<boolean>(() => submenus?.some((submenu) => isCurrentPage(submenu.link)) ?? false);

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};
</script>

<template>
  <div>
    <PlusMenuItem
      :is-active="isActive && !isDropdownOpen"
      :has-pin="hasPin"
      @click="toggleDropdown"
    >
      <template #icon>
        <slot name="icon" :is-active="isActive || isDropdownOpen" />
      </template>

      <template #text>
        <div class="flex items-center justify-between">
          <Typography
            :variant="isDropdownOpen ? 'body-bold' : 'body'"
            class="whitespace-nowrap"
          >
            <slot name="text" />
          </Typography>

          <div class="flex space-x-2">
            <Tag
              v-if="hasNewTag"
              text-color="purple"
              background-color="purple-lighter"
            >
              {{ trans('gestion.tag.new') }}
            </Tag>

            <Icon
              :svg="IconCaretDown"
              height="24"
              width="24"
              :fill="isActive ? 'black' : 'gray-darker'"
              :class="[
                'transition-transform',
                'duration-200',
                { 'rotate-180': isDropdownOpen },
              ]"
            />
          </div>
        </div>
      </template>
    </PlusMenuItem>

    <template v-if="submenus">
      <MenuList
        v-if="isDropdownOpen"
        class="plus-menu-item-group__dropdown-open"
        :class="[
          'ml-4',
          'border-y-0 border-l-2 border-r-0 border-solid border-l-gray-light',
          'p-2 pr-0',
          'flex flex-col',
        ]"
      >
        <li
          v-for="submenu in submenus"
          :key="submenu.name"
        >
          <SubmenuItem
            :link="submenu.link"
            :is-active="isCurrentPage(submenu.link)"
          >
            <Typography :variant="isCurrentPage(submenu.link) ? 'body-bold' : 'body'">
              {{ submenu.name }}
            </Typography>
          </SubmenuItem>
        </li>
      </MenuList>
    </template>

    <template v-else-if="$slots.submenus">
      <slot v-if="isDropdownOpen" name="submenus" />
    </template>
  </div>
</template>
